import { Eye, Printer } from 'react-feather';
import PageLayout from '../../../components/PageLayout';
import {
  formatDate,
  getItemById,
  getQueryString,
  hasExpired,
  OPTIONS,
} from '../../../utils/common.util';
import useSWR from 'swr';
import { useState } from 'react';
import ReportsModal from './ReportsModal';
import {
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
} from 'carbon-components-react';
import { useDebounceState } from '../../../utils/useDebounceState';
import PrintOutHead from '../../../components/PrintOutHead';
import { PrintView } from '../../../components/PrintView/print-view.component';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import Combobox from '../../../components/combobox/Combobox';
import { useUrlState } from '../../../utils/useUrlState';
import ExportReportButton from '../../../components/ExportReportButton';

const initialData = {
  dailyBillingFeeId: '',
  date: '',
  dateFrom: '',
  dateTo: '',
  studentId: '',
  academicYearId: '',
  termId: '',
  classId: '',
  studentStatus: '',
};

export default function StudentsArrearsReport() {
  const [isOpen, setIsOpen] = useState('');
  const [query, setQuery] = useUrlState(initialData);

  const { data: d } = useSWR(`daily-billing/fees/list`);
  const feeTypes = d?.dailyBillingFee ?? [];

  const [searchStudent, setSearchStudent] = useDebounceState('');
  const { data: students, isLoading: isStudentsLoading } = useSWR(
    `student/list?status=Enrolled&${getQueryString({
      query: searchStudent,
    })}`,
  );

  const { data: academicYears } = useSWR(
    'academic-year/list/?skip=0&take=100&query=',
  );
  const { data: terms } = useSWR(
    query.academicYearId
      ? `academic-year/terms/?academicYearId=${query.academicYearId}`
      : null,
  );
  const { data: classes } = useSWR('class/list');

  const mainUrl = `daily-billing/generate-arrears-advance-report`;
  const { data, isLoading, error } = useSWR(
    query.dateFrom && query.dateTo
      ? `${mainUrl}?${getQueryString(query)}`
      : null,
  );

  const studentRecords = data?.response?.studentRecords ?? [];
  const totals = data?.response?.total ?? {};
  const feeType = getItemById(feeTypes, query.dailyBillingFeeId);
  const student = getItemById(students, query.studentId);

  function isDisabled(item) {
    return hasExpired(item?.invoice?.gracePeriodEndDate);
  }

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Daily Fees', to: '/daily-fees' },
        { label: `Student Arrears & Deposit Report`, active: true },
      ]}
      title={
        <div className="d-flex align-items-center" style={{ gap: '30px' }}>
          <h1 className="h-20">Student Arrears & Deposit Report</h1>{' '}
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
            style={{ gap: '4px' }}
            onClick={() => setIsOpen('reports')}
          >
            <Eye size="14" strokeWidth="2px" />
            <span>VIEW OTHER REPORTS</span>
          </button>
        </div>
      }
      isLoading={isLoading}
    >
      {/* FILTERS  */}
      <p className="mt-20 text-12">
        Select at least a date range to show report.
      </p>
      <div
        className="mt-10"
        style={{
          display: 'grid',
          gap: '20px',
          gridTemplateColumns: 'repeat(4, 1fr)',
          alignItems: 'center',
        }}
      >
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="start-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateFrom: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateFrom: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="start-date"
            id="start-date"
            name="dateFrom"
            labelText="Start Date"
            value={query.dateFrom}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateFrom: e.target.value })}
          />
        </DatePicker>

        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="end-date-picker"
          // minDate={
          //   new Date(query.dateFrom ?? '').toISOString().split('T')[0]
          // }
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateTo: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateTo: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="end-date"
            id="end-date"
            name="dateTo"
            labelText="End Date"
            value={query.dateTo}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateTo: e.target.value })}
          />
        </DatePicker>

        <Combobox
          options={students ?? []}
          label="Select a Student"
          customStyles={{ width: '300px' }}
          value={
            query.studentId
              ? students?.find((s) => s.id === Number(query.studentId))
              : null
          }
          onChange={(option) => {
            setQuery({ studentId: option ? option.id : '' });
            setSearchStudent('');
          }}
          onSearch={setSearchStudent} // The combobox now just updates the search term state
          onClear={() => {
            setQuery({ studentId: '' });
            setSearchStudent('');
          }}
          loading={isStudentsLoading} // Pass the loading state down
          renderSelected={(option) => `${option.firstName} ${option.lastName}`}
          renderOption={(option) => `${option.firstName} ${option.lastName}`}
          labelKey="fullName"
          valueKey="id"
          searchPlaceholder="Search students..."
          placeholder=""
        />
        <Select
          labelText="Fee Type"
          value={query.dailyBillingFeeId}
          onChange={(e) => setQuery({ dailyBillingFeeId: e.target.value })}
        >
          <SelectItem selected value="" text="All" />
          {feeTypes?.map((item) => (
            <SelectItem
              key={item.id}
              text={
                item.name +
                (isDisabled(item) && item?.invoice?.status
                  ? ` - [${item.invoice.status}]`
                  : '')
              }
              value={item.id}
              disabled={isDisabled(item)}
            />
          ))}
        </Select>

        <Select
          labelText="Academic Year"
          onChange={(e) =>
            setQuery({ academicYearId: e.target.value, termId: '' })
          }
          value={query.academicYearId}
        >
          <SelectItem value="" text="All" />
          {academicYears?.map((year) => (
            <SelectItem key={year.id} value={year.id} text={year.description} />
          ))}
        </Select>
        <Select
          labelText="Term"
          disabled={!query.academicYearId}
          onChange={(e) => setQuery({ termId: e.target.value })}
          value={query.termId}
        >
          <SelectItem value="" text="All" />
          {terms?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>
        <Select
          labelText="Class"
          onChange={(e) => setQuery({ classId: e.target.value })}
          value={query.classId}
        >
          <SelectItem value="" text="All" />
          {classes?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>
        <Select
          labelText="Student Status"
          onChange={(e) => setQuery({ studentStatus: e.target.value })}
        >
          <SelectItem value="" text="All" />
          {OPTIONS.enrolled.map((status) => (
            <SelectItem key={status} value={status} text={status} />
          ))}
        </Select>
      </div>

      <div className="mt-20">
        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}
        {studentRecords?.length > 0 && (
          <>
            <PrintOutHead
              title={`Student Arrears & Deposit Report`}
              headings={[
                {
                  label: 'Student',
                  value: `${student?.firstName || ''} ${student?.lastName || 'All'}`,
                },
                {
                  label: 'Fee Type',
                  value: feeType?.name ?? 'All',
                },
                {
                  label: 'Academic Year',
                  value:
                    getItemById(academicYears, query.academicYearId)
                      ?.description ?? 'All',
                },
                {
                  label: 'Term',
                  value: getItemById(terms, query.termId)?.description ?? 'All',
                },
                {
                  label: 'Class',
                  value:
                    getItemById(classes, query.classId)?.description ?? 'All',
                },
                {
                  label: 'Student Status',
                  value: query?.studentStatus ?? 'All',
                },
                {
                  label: 'Date Range',
                  value: query.dateFrom
                    ? `${formatDate(query.dateFrom)} - ${formatDate(
                        query.dateTo,
                      )} `
                    : '-',
                },
              ]}
            />
            <PrintView>
              <div style={{ overflow: 'auto' }}>
                <table className="bx--data-table table-white">
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Class</th>
                      <th>
                        Bill <br /> (GH₵)
                      </th>
                      <th>
                        Payment <br /> (GH₵)
                      </th>
                      <th>
                        Arrears <br /> (GH₵)
                      </th>
                      <th>
                        Deposit <br /> (GH₵)
                      </th>
                      <th>
                        Applied Deposit <br /> (GH₵)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentRecords.map((entry) => {
                      return (
                        <tr key={entry.id}>
                          <td>{entry.studentName}</td>
                          <td>{entry.class}</td>
                          <td>{entry.billingAmount}</td>
                          <td>{entry.paymentAmount}</td>
                          <td>{entry.arrearsAmount}</td>
                          <td>{entry.advanceAmount}</td>
                          <td>{entry.appliedDepositAmount}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="font-bold">
                      <td colSpan={2}>Total</td>
                      <td>{totals.billingAmount}</td>
                      <td>{totals.paymentAmount}</td>
                      <td>{totals.arrearsAmount}</td>
                      <td>{totals.advanceAmount}</td>
                      <td>{totals.appliedDepositAmount}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </PrintView>
          </>
        )}
        {studentRecords?.length === 0 && (
          <p className="text-14 mt-30 text-grey">No data available.</p>
        )}

        {studentRecords?.length > 0 && (
          <div className="mt-30 d-flex gap-20 align-items-center flex-end">
            <ExportReportButton
              apiUrl={`${mainUrl}/export?${getQueryString(query)}`}
              fileName={`Daily Fee Student Arrears Report.xlsx`}
            />
            <CustomButton classes="secondary" onClick={() => window.print()}>
              <Printer size="16" strokeWidth="2px" /> &nbsp; Print
            </CustomButton>
          </div>
        )}
      </div>

      <ReportsModal
        isOpen={isOpen === 'reports'}
        closeModal={() => setIsOpen('')}
      />
    </PageLayout>
  );
}
